import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { HighlightProvider } from './contexts/HighlightContext';
import { SocketProvider } from './contexts/SocketContext';
import Layout from './components/Layout/Layout';
import { FullPageSkeleton } from './components/UI/Skeleton';
import { AuthEntry, AUTH_ROUTE_PATHS } from './components/Auth';
const OrganizationSetup = lazy(() => import('./pages/Organization/OrganizationSetup'));
const AcceptInvitation = lazy(() => import('./pages/Organization/AcceptInvitation'));
const Dashboard = lazy(() => import('./pages/Dashboard/Dashboard'));
const Reports = lazy(() => import('./pages/Reports/Reports'));
const AdvancedReports = lazy(() => import('./pages/Reports/AdvancedReports'));
const Transactions = lazy(() => import('./pages/Financial/Transactions'));
const GSTCompliance = lazy(() => import('./pages/GST/GSTCompliance'));
const StatutoryDues = lazy(() => import('./pages/GST/StatutoryDues'));
const CashflowAnalysis = lazy(() => import('./pages/Cashflow/CashflowAnalysis'));
const DataImport = lazy(() => import('./pages/DataImport/DataImport'));
const Settings = lazy(() => import('./pages/Settings/Settings'));
const Tasks = lazy(() => import('./pages/Tasks/Tasks'));
const CommentHistory = lazy(() => import('./pages/CommentHistory/CommentHistory'));
const AuditLogs = lazy(() => import('./pages/AuditLogs/AuditLogs'));
const Notifications = lazy(() => import('./pages/Notifications/Notifications'));
const Contacts = lazy(() => import('./pages/Contacts/Contacts'));
const ReceivablesPayables = lazy(() => import('./pages/ReceivablesPayables/ReceivablesPayables'));
const DocumentManagement = lazy(() => import('./pages/DocumentManagement/DocumentManagement'));
const Approvals = lazy(() => import('./pages/Approvals').then(m => ({ default: m.Approvals })));
const CreateApproval = lazy(() => import('./pages/Approvals').then(m => ({ default: m.CreateApproval })));
const ApprovalDashboard = lazy(() => import('./pages/Approvals').then(m => ({ default: m.ApprovalDashboard })));
const LandingStatic = lazy(() => import('./pages/Landing/LandingStatic'));
const MarketingLayout = lazy(() => import('./pages/Landing/MarketingLayout'));
const HomePage = lazy(() => import('./pages/Landing/HomePage'));
const ProductsPage = lazy(() => import('./pages/Landing/ProductsPage'));
const AIPage = lazy(() => import('./pages/Landing/AIPage'));
const PricingPage = lazy(() => import('./pages/Landing/PricingPage'));
const WorkspacePage = lazy(() => import('./pages/Landing/WorkspacePage'));
const ContactPage = lazy(() => import('./pages/Landing/ContactPage'));
const AboutPage = lazy(() => import('./pages/About/AboutPage'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService/TermsOfService'));
const NotFound = lazy(() => import('./pages/NotFound/NotFound'));
const WorkspaceSelection = lazy(() => import('./pages/Workspace/WorkspaceSelection'));
const CADashboard = lazy(() => import('./pages/CA/CADashboard'));
const CAClients = lazy(() => import('./pages/CA/CAClients'));
const CAClientDetail = lazy(() => import('./pages/CA/CAClientDetail'));
const CAStaff = lazy(() => import('./pages/CA/CAStaff'));
const CAGstDashboard = lazy(() => import('./pages/CA/CAGstDashboard'));
const CAComplianceCalendar = lazy(() => import('./pages/CA/CAComplianceCalendar'));
const CANotifications = lazy(() => import('./pages/CA/CANotifications'));
const CAWorkspaceOutlet = lazy(() => import('./pages/CA/CAWorkspaceOutlet'));
const CAWorkspaceSetup = lazy(() => import('./pages/CA/CAWorkspaceSetup'));
import ErrorBoundary from './components/UI/ErrorBoundary';
import { queryClient } from './config/queryClient';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isBootstrapping } = useAuth();

  if (isBootstrapping) {
    return <FullPageSkeleton />;
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <SocketProvider>
            <HighlightProvider>
              <Suspense fallback={<FullPageSkeleton />}>
              <Routes>
              <Route path="/" element={<LandingStatic />} />
              <Route path="/homepage" element={<MarketingLayout />}>
                <Route index element={<HomePage />} />
                <Route path="products" element={<ProductsPage />} />
                <Route path="ai" element={<AIPage />} />
                <Route path="pricing" element={<PricingPage />} />
                <Route path="workspace" element={<WorkspacePage />} />
                <Route path="contact" element={<ContactPage />} />
              </Route>
              <Route path="/about" element={<AboutPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              {AUTH_ROUTE_PATHS.map((path) => (
                <Route key={path} path={path} element={<AuthEntry />} />
              ))}
              <Route path="/forgotPassword" element={<Navigate to="/forgot-password" replace />} />
              <Route path="/accept-invitation" element={<AcceptInvitation />} />
              <Route path="/organization/setup" element={
                <ProtectedRoute>
                  <OrganizationSetup />
                </ProtectedRoute>
              } />
              <Route path="/workspace/select" element={
                <ProtectedRoute>
                  <WorkspaceSelection />
                </ProtectedRoute>
              } />
              <Route path="/ca" element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }>
                <Route element={<CAWorkspaceOutlet />}>
                  <Route path="workspace-setup" element={<CAWorkspaceSetup />} />
                  <Route path="dashboard" element={<CADashboard />} />
                  <Route path="clients" element={<CAClients />} />
                  <Route path="clients/:organizationId" element={<CAClientDetail />} />
                  <Route path="clients/:organizationId/tasks" element={<CAClientDetail />} />
                  <Route path="clients/:organizationId/documents" element={<CAClientDetail />} />
                  <Route path="clients/:organizationId/timeline" element={<CAClientDetail />} />
                  <Route path="clients/:organizationId/reviews" element={<CAClientDetail />} />
                  <Route path="staff" element={<CAStaff />} />
                  <Route path="gst" element={<CAGstDashboard />} />
                  <Route path="compliance/calendar" element={<CAComplianceCalendar />} />
                  <Route path="notifications" element={<CANotifications />} />
                </Route>
              </Route>
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }>
                <Route index element={<Dashboard />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="reports" element={<Reports />} />
                <Route path="reports/advanced" element={<AdvancedReports />} />
                <Route path="transactions" element={<Transactions />} />
                <Route path="receivables-payables" element={<ReceivablesPayables />} />
                <Route
                  path="transaction-requests"
                  element={<Navigate to="/approvals?tab=transaction-requests" replace />}
                />
                <Route path="gst" element={<GSTCompliance />} />
                <Route path="gst/statutory" element={<StatutoryDues />} />
                <Route path="cashflow" element={<CashflowAnalysis />} />
                <Route path="import" element={<DataImport />} />
                <Route path="tasks" element={<Tasks />} />
                <Route path="contacts" element={<Contacts />} />
                <Route path="documents" element={<DocumentManagement />} />
                <Route path="notifications" element={<Notifications />} />
                <Route path="comment-history" element={<CommentHistory />} />
                <Route path="audit-logs" element={<AuditLogs />} />
                <Route path="admin/users" element={<Navigate to="/settings?tab=roles" replace />} />
                <Route path="approvals" element={<Approvals />} />
                <Route path="approvals/create" element={<CreateApproval />} />
                <Route path="approvals/dashboard" element={<ApprovalDashboard />} />
                <Route path="settings" element={<Settings />} />
              </Route>
              
              {/* Direct module routes for easier access */}
              <Route path="/reports" element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }>
                <Route index element={<Reports />} />
              </Route>
              
              <Route path="/transactions" element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }>
                <Route index element={<Transactions />} />
              </Route>

              <Route path="/receivables-payables" element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }>
                <Route index element={<ReceivablesPayables />} />
              </Route>

              <Route
                path="/transaction-requests"
                element={<Navigate to="/approvals?tab=transaction-requests" replace />}
              />

              <Route path="/expense-requests" element={<Navigate to="/approvals?tab=transaction-requests" replace />} />
              
              <Route path="/gst" element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }>
                <Route index element={<GSTCompliance />} />
                <Route path="statutory" element={<StatutoryDues />} />
              </Route>
              
              <Route path="/cashflow" element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }>
                <Route index element={<CashflowAnalysis />} />
              </Route>
              
              <Route path="/tasks" element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }>
                <Route index element={<Tasks />} />
              </Route>
              
              <Route path="/contacts" element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }>
                <Route index element={<Contacts />} />
              </Route>
              
              <Route path="/documents" element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }>
                <Route index element={<DocumentManagement />} />
              </Route>
              
              <Route path="/approvals" element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }>
                <Route index element={<Approvals />} />
                <Route path="create" element={<CreateApproval />} />
                <Route path="dashboard" element={<ApprovalDashboard />} />
              </Route>
              
              <Route path="/settings" element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }>
                <Route index element={<Settings />} />
              </Route>
              
              <Route path="/admin/users" element={<Navigate to="/settings?tab=roles" replace />} />
              
              
              <Route path="/audit-logs" element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }>
                <Route index element={<AuditLogs />} />
              </Route>
              
              <Route path="/comment-history" element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }>
                <Route index element={<CommentHistory />} />
              </Route>
              
              <Route path="/notifications" element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }>
                <Route index element={<Notifications />} />
              </Route>
              
              <Route path="/import" element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }>
                <Route index element={<DataImport />} />
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
            <Toaster position="top-right" />
            </HighlightProvider>
          </SocketProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;